// Core
import React from 'react';
import { Box, Flex, Grid, Text, Image, useColorModeValue, useColorMode, useDisclosure } from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';
import { getInitials, getDaysLeft } from 'services/Utils';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';
import glow from 'assets/img/glow-10.png';

// Components
import Card from 'components/Card/Card';
import { CalendarIcon, UpdateAvatarIcon } from 'components/Icons/Icons';
import Profile from './components/Profile';
import Address from './components/Address';
import UpdateAvatarModal from './components/UpdateAvatar';

const Account = (props) => { 
  const { userData, userRefresh, setUserData } = props;
  const bg = useColorModeValue('white', '#0A1027');
  const border = useColorModeValue('1px solid #CBE7FF', '1px solid #0a1025');
  const boxShadow = useColorModeValue('unset', '0px 1px 3px 0px rgba(0, 0, 0, 0.10),0px 1px 2px 0px rgba(0, 0, 0, 0.06)');
  const emailColor = useColorModeValue('#262D47', '#9FABCB');
  const boxShadowAccessDays = useColorModeValue('unset', '0px 4px 12px 0px rgba(0, 122, 255, 0.50)');
  const iconColor = useColorModeValue('#0E1633', 'white');
  const iconBgColor = useColorModeValue('#F5FAFF', '#0D234D');
  const bgUpdateAvatarIcon = useColorModeValue('white', '#0E1633');

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Card padding={{base: '16px 12px', md: '24px 24px 32px 24px'}} overflow="hidden" border={border} boxShadow={boxShadow}>

        <Grid alignItems="center" templateColumns={{ sm: '1fr', lg: '1fr 294px' }} gap="10px" templateRows='1fr' zIndex="2">
          <Flex gap="16px" alignItems="center">
            <Box 
              w={{base: '54px', md: '84px', lg: '98px'}} 
              h={{base: '54px', md: '84px', lg: '98px'}} 
              borderRadius="full" 
              position="relative"
              borderWidth={{base: '2px', md: '3px', lg: '4px'}} 
              borderColor="#099BFF"
              >
              { userData?.image?.length > 3 ? (
                  <Image 
                    src={userData?.image} 
                    w={{base: '50px', md: '78px', lg: '90px'}} 
                    h={{base: '50px', md: '78px', lg: '90px'}}  
                    objectFit="cover" borderRadius="full" />
              ) : (
                <Box className={userData?.gender === 'Male' ? 
                  'gender-male' : userData?.gender === 'Female' ? 
                  'gender-female' : userData?.gender === 'Transgender' ? 
                  'gender-other' : 'gender-none' } maxW="100%" h="100%" borderRadius="full">
                  <Text 
                    fontSize={{base: '24px !important', md: '32px !important', lg: '42px !important'}} 
                    pt={{base: '11px !important', md: '20px !important', lg: '20px !important'}} 
                    >{getInitials(userData?.firstName, userData?.lastName)}</Text>
                </Box>
              )}

              <Box
                cursor="pointer" 
                position="absolute" 
                right="-4px"
                bg={bgUpdateAvatarIcon} 
                bottom="-4px"
                w={{base: '18px', md: '29px', lg: '34px'}}
                h={{base: '18px', md: '29px', lg: '34px'}}
                p={{base: '0px 5px', md: '2px 8px',  lg: '4px 9px'}}
                borderRadius="full" 
                onClick={onOpen}
                >
                  <UpdateAvatarIcon
                    color="#099BFF"
                    mt={{base: '-8px', md: 'unset'}}
                    fontSize={{base: '9px', md: '15px', lg: '18px'}}
                  />
              </Box>

              <UpdateAvatarModal 
                userData={userData} 
                setUserData={setUserData}
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                />
            </Box>

            <Box>
              <Text variant="title-lg" fontSize="24px">{userData?.firstName} {userData?.lastName}</Text>
              <Text fontWeight="500" color={emailColor}>{userData?.email}</Text>
            </Box>
          </Flex>

          <Card padding="7px" overflow="hidden" borderRadius="12px" zIndex="3" boxShadow={boxShadow}>
            { isDark ? <Lines className="abstract-lines" style={{zIndex: '1', left: '-40%'}} />
            : <LinesWhite className="abstract-lines" style={{zIndex: '1', left: '-40%', opacity: 0.3}} /> }
            
            <Flex justifyContent="space-between" alignItems="center" zIndex="2">
              <Flex gap="8px" alignItems="center">
                <Box
                  borderRadius="8px"
                  p="4px"
                  w="32px" h="32px"
                  bg={iconBgColor}>
                  <CalendarIcon fontSize="24px" color={iconColor} />
                </Box>
                <Text variant="title-lg" fontSize="15px">Access days left</Text>
              </Flex>

              <Text
                textAlign="center"
                minW="40px"
                p="5px 8px"
                variant="title-lg" 
                fontSize="15px"
                borderRadius="16px"
                bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                color="white"
                boxShadow={boxShadowAccessDays}
              >{ userData?.accessDays?.type === 'One Off' ? userData?.accessDays?.days || 0 : getDaysLeft(userData?.activeSubscription?.periodEnd ?? new Date())}</Text>
            </Flex>

          </Card>
        </Grid>

        <Box bg="#099BFF" borderRadius="full" bottom="-100%" filter="blur(100px)" w="100%" h="100%" position="absolute" left="0%" />
        <Image src={glow} alt="Glow" w="80%" position="absolute" bottom={{base: '-45%', md: '-40%', lg: '-73%'}} right="-8%" />
      </Card>

      { userData && ( <Profile userData={userData} />) }

      { userData && ( <Address userData={userData} userRefresh={userRefresh} setUserData={setUserData} />) }
    </>
  );
};

export default Account;
